import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useDataGroupInviteStore } from '@/gp/DataGroupInviteStore'
import { useUserStore } from '@/gp/UserStore'
import { useFamilyGroupMemberStore } from '@/gp/FamilyGroupMemberStore'
import { useFamilyGroupInviteStore } from '@/gp/FamilyGroupInviteStore'
import { useDataGroupMemberStore } from '@/gp/DataGroupMemberStore'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { usePersonStore } from '@/rd/PersonStore'
import { isDefined } from "@/util/TypeScriptUtil"
import { chunk, groupBy, maxBy } from 'lodash'
import { Person } from '@/rd/ResearchDataModel'
import { DataGroup, DataGroupInvite, DataGroupMember, FamilyGroup, FamilyGroupInvite, FamilyGroupMember, GroupMemberRole, InvitationResult, ItemPermissions, PrincipalType, User } from '@/gp/GroupAdminModel'
import { useTokenManager } from '@/auth/TokenManager'
import { useViewFamilyStore } from '@/rd/ViewFamilyStore'
import { useUserSupervisorStore } from '@/gp/UserSupervisorStore'
import { LoadMode } from '@/util/AsyncData'


/**
 * Represents a person who is, is suggested as, or is invited to be a member of a family group, and/or a profile that is, or
 * is suggested or invited to be shared with the family group. Focusing the user experience on a *person*, and letting the 
 * user view/manage whether they are included in the group as a member, a shared profile, or both helps match the user's 
 * mental model, even though the mechanisms are not focused on the person at the API level (the "person" is fluid, as it 
 * depends on profile matching).
 */
export class PotentialMember {
    static readonly UserKeyPrefix = "U:"

    /**
     * The id of the profile group (if any) for this potential member. The profile group may be determined by the user's personal
     * profile, profile sharing, a sharing invite, or by a family group invite. The profile group itself may not be visible to the 
     * current user.
     */
    profileGroupId?: string

    /**
     * The id of the user (if any) for this potential member. The user may be determined by the family group member, profile, or 
     * invite for this person. If a profile id is specified, this is the visible user (if any) for whom the profile is their 
     * personal profile. If a member is present, this is the member user.
     */
    userId?: string

    /**
     * The user for this potential member, if visible.
     */
    user?: User

    /**
     * The family group member (if any) for this potential member.
     */
    member?: FamilyGroupMember

    /**
     * The profile group (if visible) for this potential member.
     */
    profileGroup?: DataGroup

    /**
     * The primary person (if visible) of the profile group for this potential member.
     */
    profilePerson?: Person

    /**
     * The data group member (if any) by which the profile group is shared with the family group.
     */
    sharedProfileMember?: DataGroupMember

    /**
     * The most recent family group invite (if any) for the user.
     */
    lastUserInvite?: FamilyGroupInvite

    /**
     * The most recent family group invite (if any) for the profile group.
     */
    lastProfileInvite?: FamilyGroupInvite

    /**
     * The most recent sharing invite (if any) for the profile group.
     */
    lastSharingInvite?: DataGroupInvite

    /**
     * The permissions the auth user has for the profile group.
     */
    profilePermissions = ItemPermissions.None

    /**
     * Indicates whether the auth user is the supervisor of the user.
     */
    isUserSupervisor = false

    /**
     * Indicates whether this potential member is selected by the user for some action.
     */
    selected = false

    /**
     * Gets a value representing the unique identity of a potential member. If a profile is present, the id is always the
     * id of the profile person (or the profile group if the group is not visible). The id is only a user id if there is no 
     * profile defined (e.g. user is a member or invited, but has not shared their user profile and the profile is not visible 
     * to the current user). A prefix is used to prevent (rare, but technically possible) collisions between profile person 
     * ids and user ids. While a profile group id and profile person id can both be used to identify a profile, the person id
     * is used whenever available to make it easy to match with view person match ids. If the profile person id is not available,
     * (because the profile group is not visible) it won't match with any visible view person anyway.
     */
    get id() { return this.profilePerson?.id ?? this.profileGroupId ?? PotentialMember.UserKeyPrefix + this.userId! }

    /**
     * The display name for this potential member (user, profile person, invite recipient).
     */
    get displayName() { return this.user?.displayName ?? this.profilePerson?.displayName ?? this.lastGroupInvite?.recipientDisplayName ?? '(unknown)' }

    get assumeDeceased() { return !!this.profilePerson?.displayProperties.assumeDeceased }

    /**
     * Indicates whether the profile for this potential member is shared with the family group.
     */
    get isProfileShared() { return !!this.sharedProfileMember }

    get isProfileOwner() { return (this.profilePermissions & ItemPermissions.Owner) > 0 }

    get canModifyProfile() { return (this.profilePermissions & ItemPermissions.Modify) > 0 }

    get lastGroupInvite() { return maxBy([this.lastProfileInvite, this.lastUserInvite].filter(isDefined), inv => inv.lastSentDate!.valueOf())}

    get hasPendingGroupInvite() { return this.lastProfileInvite?.isPending || this.lastUserInvite?.isPending }

    get hasPendingInvite() { return this.hasPendingGroupInvite || this.lastSharingInvite?.isPending }

    /**
     * Indicates whether profile sharing is locked, either because the profile is shared and cannot be removed because a profile 
     * invite is pending, or because the profile is not shared and a sharing invite is pending.
     */
    get isProfileSharingLocked() { return this.lastProfileInvite?.isPending || this.lastSharingInvite?.isPending }

    /**
     * Indicates whether this potential member can be invited to the family group (assuming appropriate permissions
     * on both the profile person and the family group).
     */
    get canBeInvited() { return !this.member && 
        (!this.lastGroupInvite || this.lastGroupInvite.result == InvitationResult.Cancelled) && 
        !this.profilePerson?.displayProperties.assumeDeceased }

    /**
     * Indicates whether this person is only a suggested member, i.e. no action has been taken to invite them personally or 
     * share their profile.
     */
    get isSuggested() { return !this.member && !this.sharedProfileMember && !this.hasPendingInvite }

    constructor(profileGroupId?: string, userId?: string) {
        if (!profileGroupId && !userId)
            throw new Error('PotentialMember must have either a profile group id or user id')

        this.profileGroupId = profileGroupId
        this.userId = userId
    }
}

export enum RelationshipCategory { // includes spouses
    None = 0,
    Ancestors,
    Scope,
    Child,
    Grandchild,
}

export type MemberListMode = 'share' | 'invite'

export const MaxAncestorGenerations = 3
export const MaxDescendantGenerations = 2

export const potentialMemberIcons = {
  person: new URL('@/assets/icons/person_gray_150.png', import.meta.url).href,
  suggested: new URL('@/assets/icons/person_dotted_80.png', import.meta.url).href,
}

export function useFamilyGroupMemberManager() {
    const viewPersonStore = useViewPersonStore()
    const viewFamilyStore = useViewFamilyStore()
    const personStore = usePersonStore()
    const dataGroupStore = useDataGroupStore()
    const dataGroupMemberStore = useDataGroupMemberStore()
    const dataGroupInviteStore = useDataGroupInviteStore()
    const familyGroupMemberStore = useFamilyGroupMemberStore()
    const familyGroupInviteStore = useFamilyGroupInviteStore()
    const userStore = useUserStore()
    const userSupervisorStore = useUserSupervisorStore()
    const tokenManager = useTokenManager()

    /**
     * Gets a list of potential members for the specified family group, including current members, shared profiles, invited
     * users and profiles, and profiles invited for sharing.
     */
    async function getPotentialMembersAsync(familyGroup: FamilyGroup) {
        // NOTE: this call loads FG members plus user/profiledatagroup (and implicitly group owner, since it is always the user)
        const getMembers = familyGroupMemberStore.getMembersForFamilyGroupsAsync([familyGroup.id])
        const getDataGroupMembers = dataGroupMemberStore.getMembersForFamilyGroupsAsync([familyGroup.id])
        const getInvites = familyGroupInviteStore.getInvitesForFamilyGroupsAsync([familyGroup.id])
        const getSharingInvites = dataGroupInviteStore.getInvitesForFamilyGroupAsync(familyGroup.id)

        await Promise.all([
            getMembers,
            getDataGroupMembers,
            getInvites,
            getSharingInvites,
        ])

        const members = await getMembers
        const invites = await getInvites
        const sharingInvites = (await getSharingInvites).filter(inv => inv.principalType == PrincipalType.FamilyGroup)

        const sharedProfileMemberMap = new Map((await getDataGroupMembers)
            .filter(dm => dataGroupStore.getAsyncGroup(dm.dataGroupId)!.data!.isProfile) // loaded inline with DG members
            .map(dm => [dm.dataGroupId!, dm])) // key = profile group id
        
        console.log(`Loaded ${members.length} members for family group ${familyGroup.id}`)
        console.log(`Loaded ${sharedProfileMemberMap.size} shared profiles for family group ${familyGroup.id}`)
        console.log(`Loaded ${invites.length} user invites for family group ${familyGroup.id}`)
        console.log(`Loaded ${sharingInvites.length} sharing invites for family group ${familyGroup.id}`)

        // load user profile groups
        const allUsers = members.map(m => userStore.getAsyncUser(m.userId)!.data) // loaded inline with FG members
            .concat(invites.map(inv => userStore.getAsyncUser(inv.recipientUserId)?.data)) // loaded inline with FG invites
            .filter(isDefined) // invited may not be visible to current user
        await dataGroupStore.getGroupListAsync(allUsers.map(u => u.profileDataGroupId)) // NOT loaded inline                

        // get last FG invite for each recipient user (MAY include a profile group id)
        const userInvites = invites.filter(inv => inv.recipientUserId)
        const lastUserInvites = lastFor(userInvites, inv => inv.recipientUserId, inv => inv.lastSentDate?.valueOf())
        const lastUserInvitesMap = new Map(lastUserInvites.map(inv => [inv.recipientUserId!, inv]))

        // get last FG invite for each profile group that does NOT have a recipient user (MUST have a profile group in that case)
        const profileInvites = invites.filter(inv => !inv.recipientUserId)
        const lastProfileInvites = lastFor(profileInvites, inv => inv.profileDataGroupId, inv => inv.lastSentDate?.valueOf())
        const lastProfileInvitesMap = new Map(lastProfileInvites.map(inv => [inv.profileDataGroupId!, inv]))

        // get last sharing invite for each profile group
        const lastSharingInvites = lastFor(sharingInvites, inv => inv.dataGroupId, inv => inv.lastSentDate?.valueOf())
        const lastSharingInvitesMap = new Map(lastSharingInvites.map(inv => [inv.dataGroupId!, inv]))

        const potentialMembers: PotentialMember[] = []
        const usedUserIds = new Set<string>()
        const usedProfileGroupIds = new Set<string>()

        // create potential members for FG members
        for (const m of members) {
            const u = userStore.getAsyncUser(m.userId)!.data! // loaded inline with FG members
            const pg = dataGroupStore.getAsyncGroup(u.profileDataGroupId)?.data // loaded inline with FG members

            const pm = new PotentialMember(u.profileDataGroupId, m.userId);
            pm.member = m
            pm.profileGroup = pg
            pm.user = u
            pm.lastUserInvite = lastUserInvitesMap.get(u.id!)
            pm.lastProfileInvite = lastProfileInvitesMap.get(u.profileDataGroupId!)
            potentialMembers.push(pm)

            usedUserIds.add(m.userId!)            
            // NOTE: a user can be a FG member WITHOUT sharing their profile, so profile may not be visible to current user
            if (u.profileDataGroupId) {
                pm.sharedProfileMember = sharedProfileMemberMap.get(u.profileDataGroupId)
                pm.lastSharingInvite = lastSharingInvitesMap.get(u.profileDataGroupId)
                usedProfileGroupIds.add(u.profileDataGroupId)
            }
        }

        // create potential members for REMAINING shared profile members
        const remainingShareProfileMembers = [...sharedProfileMemberMap.values()]
            .filter(dm => !usedProfileGroupIds.has(dm.dataGroupId!))

        for (const dm of remainingShareProfileMembers) {
            const dg = dataGroupStore.getAsyncGroup(dm.dataGroupId)!.data! // loaded inline with DG members
            const owner = userStore.getAsyncUser(dg.ownerId)?.data // loaded inline with DG members
            const u = owner?.profileDataGroupId == dg.id ? owner : undefined // user is owner of profile group

            const pm = new PotentialMember(dm.dataGroupId, u?.id);
            // pm.member = NOT a FG member
            pm.profileGroup = dg
            pm.user = u
            pm.sharedProfileMember = dm
            pm.lastProfileInvite = lastProfileInvitesMap.get(dm.dataGroupId!)
            pm.lastSharingInvite = lastSharingInvitesMap.get(dm.dataGroupId!)
            potentialMembers.push(pm)

            if (u?.id) {
                pm.lastUserInvite = lastUserInvitesMap.get(u.id)
                usedUserIds.add(u.id)
            }
            usedProfileGroupIds.add(dm.dataGroupId!)
        }

        // create potential members for REMAINING user invites
        const remainingLastUserInvites = lastUserInvites
            .filter(inv => !usedUserIds.has(inv.recipientUserId!))
            .filter(inv => !inv.profileDataGroupId || !usedProfileGroupIds.has(inv.profileDataGroupId))
            .filter(inv => inv.isPending) // only include pending invites

        for (const inv of remainingLastUserInvites) {
            const u = userStore.getAsyncUser(inv.recipientUserId)!.data // loaded inline with FG invite
            const pg = dataGroupStore.getAsyncGroup(u?.profileDataGroupId)?.data // loaded above

            const pm = new PotentialMember(u?.profileDataGroupId, inv.recipientUserId);
            // pm.member = NOT a FG member
            pm.profileGroup = pg
            pm.user = u
            // pm.sharedProfileMember = user profile NOT shared
            pm.lastUserInvite = inv
            potentialMembers.push(pm)

            usedUserIds.add(inv.recipientUserId!)
            if (u?.profileDataGroupId) {
                pm.lastProfileInvite = lastProfileInvitesMap.get(u.profileDataGroupId)
                pm.lastSharingInvite = lastSharingInvitesMap.get(u.profileDataGroupId)
                usedProfileGroupIds.add(u.profileDataGroupId)
            }
        }

        // create potential members for REMAINING profile invites
        const remainingProfileInvites = invites
            .filter(inv => !inv.recipientUserId)
            .filter(inv => inv.profileDataGroupId && !usedProfileGroupIds.has(inv.profileDataGroupId))
            .filter(inv => inv.isPending) // only include pending invites

        for (const inv of remainingProfileInvites) {
            const pg = dataGroupStore.getAsyncGroup(inv.profileDataGroupId)?.data // loaded inline with FG invites
            const owner = userStore.getAsyncUser(pg?.ownerId)?.data // loaded inline with FG invites
            const u = owner?.profileDataGroupId == pg?.id ? owner : undefined // user is owner of profile group

            const pm = new PotentialMember(inv.profileDataGroupId, u?.id);
            // pm.member = NOT a FG member
            pm.profileGroup = pg
            pm.user = u
            // pm.sharedProfileMember = profile NOT shared
            pm.lastProfileInvite = inv
            pm.lastSharingInvite = lastSharingInvitesMap.get(inv.profileDataGroupId!)
            potentialMembers.push(pm)

            if (u?.id) {
                pm.lastUserInvite = lastUserInvitesMap.get(u?.id)
                usedUserIds.add(u.id)
            }
            usedProfileGroupIds.add(inv.profileDataGroupId!)
        }

        // create potential members for REMAINING sharing invites
        for (const inv of sharingInvites) {
            if (usedProfileGroupIds.has(inv.dataGroupId!))
                continue; // potential already created for this profile

            const pg = dataGroupStore.getAsyncGroup(inv.dataGroupId)?.data // loaded inline with sharing invites
            const owner = userStore.getAsyncUser(pg?.ownerId)?.data // loaded inline with sharing invites
            const u = owner?.profileDataGroupId == pg?.id ? owner : undefined // user is owner of profile group

            if (u && usedUserIds.has(u.id!))
                continue; // potential already created for this user

            const pm = new PotentialMember(inv.dataGroupId, u?.id)
            // pm.member = NOT a FG member
            pm.profileGroup = pg
            pm.user = u
            // pm.sharedProfileMember = profile NOT shared
            // pm.lastUserInvite = no user invite for this user or profile
            // pm.lastProfileInvite = no profile invite for this profile
            pm.lastSharingInvite = inv
            potentialMembers.push(pm)
        }

        if (familyGroup.scopeFamilyId) {
            // NOTE: This will often load most or all view persons for potential members, reducing those that need to be loaded below
            const scopeViewPersons = await getScopeViewPersonsAsync(familyGroup)
            const profilePersonIds = new Set(potentialMembers.map(pm => pm.profileGroup?.startItemId).filter(isDefined))
            const suggestedMembers = scopeViewPersons.flatMap(vp => vp.matchIds)
                .filter(id => !profilePersonIds.has(id))
                .map(id => personStore.getAsyncPerson(id)?.data) // loaded inline with view persons
                .filter(isDefined)
                .filter(p => p.profile && !p.placeholder) // TODO: we should not load placeholder matches inline!
                .map(p => new PotentialMember(p.groupId))

            // load suggested member profile groups
            const suggestedProfileGroupIds = suggestedMembers.map(pm => pm.profileGroupId)
            await dataGroupStore.getGroupListAsync(suggestedProfileGroupIds)
            for (const pm of suggestedMembers) {
                pm.profileGroup = dataGroupStore.getAsyncGroup(pm.profileGroupId)?.data
                pm.user = pm.profileGroup?.isUserProfile ? userStore.getAsyncUser(pm.profileGroup.ownerId)?.data : undefined
            }

            potentialMembers.push(...suggestedMembers)
        }
        
        // ensure view persons loaded, and get profile persons (loaded inline)
        const allPersonIds = potentialMembers.map(pm => pm.profileGroup?.startItemId).filter(isDefined)
        await viewPersonStore.getPersonListAsync(allPersonIds)

        const personMap = new Map(personStore.getAsyncPersonList(allPersonIds).map(a => a.data).filter(isDefined)
            .map(p => [p.id!, p]))
        for (const pm of potentialMembers) {
            if (pm.profileGroup?.startItemId) {
                pm.profilePerson = personMap.get(pm.profileGroup.startItemId)
            }
        }

        // load profile permissions
        const memberProfileGroupIds = potentialMembers.map(pm => pm.profileGroupId).filter(isDefined)
        await dataGroupStore.getPermissionsListAsync(memberProfileGroupIds)
        for (const pm of potentialMembers.filter(pm => pm.profileGroupId)) {
            pm.profilePermissions = dataGroupStore.getLoadedPermissions(pm.profileGroupId)
        }

        // load supervised users
        if (!tokenManager.current.isSupervised) {
            const authUserId = tokenManager.current.userId
            const supervisedUserIds = (await userSupervisorStore.getAsyncKeyListForSupervisor(authUserId)?.whenLoadCompleted)?.keys ?? []
            if (supervisedUserIds.length) {
                for (const pm of potentialMembers.filter(pm => pm.userId)) {
                    pm.isUserSupervisor = supervisedUserIds.includes(pm.userId!)
                }
            }
        }

        return potentialMembers
    }

    function lastFor<T, V>(items: T[], keySelector: (item: T) => string | undefined, valueSelector: (item: T) => V) {
        return Object.values(groupBy(items.filter(keySelector), keySelector))
            .map(g => maxBy(g, valueSelector)!)
    }

    async function getScopeViewPersonsAsync(familyGroup: FamilyGroup) {
        if (!familyGroup.scopeFamilyId)
            throw new Error('Family group does not have a scope family id')

        const scopeFamily = await viewFamilyStore.getFamilyAsync(familyGroup.scopeFamilyId)
        if (!scopeFamily)
            return []

        const scopeViewPersons = viewPersonStore.getLoadedPersonList(scopeFamily?.spouseIds)

        await viewPersonStore.ensureAncestorsLoadedAsync([scopeFamily?.fatherId, scopeFamily?.motherId], MaxAncestorGenerations)

        scopeViewPersons.push(
            ...viewPersonStore.getLoadedAncestors(scopeFamily?.fatherId, MaxAncestorGenerations),
            ...viewPersonStore.getLoadedAncestors(scopeFamily?.motherId, MaxAncestorGenerations))

        // load descendants (up to max generations) and ALL spouses, including those of the last generation
        // HACK: this function does not load spouses of the last generation, so +1 to make sure we get them
        await viewFamilyStore.ensureDescendantsLoadedAsync(familyGroup.scopeFamilyId, MaxDescendantGenerations + 1)
        const descendants = [...viewFamilyStore.getLoadedDescendants(familyGroup.scopeFamilyId, MaxDescendantGenerations)] // stop at max
        scopeViewPersons.push(...descendants)

        for (const vp of descendants) {
            const spouseFamilies = viewPersonStore.getLoadedSpouseFamilies(vp.id!).spouseFamilies
            const spouseIds = spouseFamilies.map(sf => sf.spouseOf(vp.id!)).filter(isDefined)
            const spouses = viewPersonStore.getLoadedPersonList(spouseIds).filter(isDefined)
            scopeViewPersons.push(...spouses)
        }

        return scopeViewPersons
    }

    async function shareProfilesAsync(familyGroupId: string, profileGroupIds: string[]) {
        for (const batch of chunk(profileGroupIds, 5)) {
            const addBatch = batch.map(profileGroupId => {
                const dm = new DataGroupMember()
                dm.dataGroupId = profileGroupId
                dm.principalType = PrincipalType.FamilyGroup
                dm.principalId = familyGroupId
                dm.role = GroupMemberRole.Viewer
                return dataGroupMemberStore.addAsync(profileGroupId, dm)
            })
            await Promise.all(addBatch)
        }
    }

        // const nonOwnedProfileGroups = members.filter(pm => !pm.isProfileOwner).map(pm => pm.profileGroup!)

        // for (const batch of chunk(nonOwnedProfileGroups, 5)) {
        //     const sendBatch = batch.map(pg => {
        //         const inv = new DataGroupInvite()
        //         inv.dataGroupId = pg.id!
        //         inv.principalType = PrincipalType.FamilyGroup
        //         inv.principalId = familyGroupId
        //         inv.senderMessage = requestMessage
        //         return dataGroupInviteStore.sendAsync(inv)
        //     })
        //     await Promise.all(sendBatch)
        // }

    async function removeSharedProfilesAsync(familyGroupId: string, profileGroupIds: string[]) {
        const dms = await dataGroupMemberStore.getMembersForFamilyGroupsAsync([familyGroupId])
        const dmsToRemove = dms.filter(dm => profileGroupIds.includes(dm.dataGroupId!))

        for (const batch of chunk(dmsToRemove, 5)) {
            const removeBatch = batch.map(dm => dataGroupMemberStore.deleteAsync(dm))
            await Promise.all(removeBatch)
        }
    }

    return {
        getPotentialMembersAsync,
        shareProfilesAsync,
        removeSharedProfilesAsync,
    }
}

